import AvailableDomainBody from "components/availableDomainBody/AvailableDomainBody";
import Page from "components/page/Page";
import PageTitle from "components/pageTitle/PageTitle";
import PageWrapper from "components/pageWrapper/PageWrapper";
import PurchaseDropDown from "components/purchaseDropDown/PurchaseDropDown";
import TakenDomainBody from "components/takenDomainBody/TakenDomainBody";
import WhoisButton from "components/whoisButton/WhoisButton";
import { useAppDispatch, useAppSelector } from "hooks";
import { memo, useCallback, useEffect, useMemo, useState } from "react";
import { Spinner, Alert } from "react-bootstrap";
import Helmet from "components/headHelmet/HeadHelmet";
import { useParams } from "react-router-dom";
import domainSlice from "reducers/domainSlice";
import {
  domainDetailsSelector,
  getDomainDetailsErrorSelector,
  gettingDomainDetailsSelector,
  resellersSelector,
} from "selectors/domainSelectors";
import DomainDetails from "types/domainDetails";
import styled from "styled-components";
import { getSiteUrl } from "utility/urlUtility";

const StyledPageTitle = styled(PageTitle)<{
  $registered: boolean;
  $loaded: boolean;
}>`
  flex: 1;
  margin-bottom: 0;
  color: ${(props: any) =>
    props.$loaded ? (props.$registered ? "#c60404" : "#21b400") : "#222222"};
`;

const Header = styled.div<{ $showBrandContent: boolean }>`
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: space-between;
  width: 100%;
  min-height: 48px;
  margin-bottom: ${(props: any) => (props.$showBrandContent ? "24px" : "0")};

  @media only screen and (max-width: 992px) {
    padding-top: 10px;
  }

  @media only screen and (max-width: 500px) {
    align-items: flex-start;
  }
`;

const Status = styled.div`
  display: flex;
  justify-content: flex-end;
  margin-left: 12px;
  flex-shrink: 0;
`;

const SpinnerWrapper = styled.div`
  color: #28c604;
  font-size: initial;

  > div {
    font-size: 1.5rem;
    width: 30px;
    height: 30px;
  }
`;

const ScoreLine = styled.div`
  display: inline-flex;
  align-self: flex-start;
  align-items: center;
  flex-wrap: wrap;
  gap: 8px;
  margin-bottom: 24px;
  padding: 8px 14px;
  border-radius: 14px;
  background: #ecfdf3;
`;

const ScoreValue = styled.strong`
  font-size: 1rem;
  color: #067a00;
  font-weight: 700;
`;

const ScorePotential = styled.span`
  font-size: 0.95rem;
  color: #067a00;
  font-weight: 500;
`;

const ScoreSeparator = styled.span`
  color: #067a00;
  font-weight: 500;
`;

const MetaLine = styled.p`
  margin: 0 0 28px;
  color: #111827;
  font-size: 1.05rem;
  font-weight: 500;
  line-height: 1.4;
`;

const ThemeLine = styled.p`
  margin: 0 0 44px;
  font-size: 0.95rem;
`;

const ThemeLineLabel = styled.span`
  color: #6b7280;
  font-size: 0.8rem;
  font-weight: 500;
  margin-right: 8px;
`;

const ThemeToken = styled.span`
  display: inline-block;
  padding: 4px 9px;
  border-radius: 999px;
  background: #f3fdf1;
  color: #075f00;
  font-size: 0.87rem;
  font-weight: 500;
  margin-right: 6px;
  margin-top: 6px;

  &:last-child {
    margin-right: 0;
  }
`;

const AboutSection = styled.section`
  margin-top: 0;
  margin-bottom: 24px;
`;

const AboutTitle = styled.h2`
  font-size: 1.15rem;
  font-weight: 600;
  margin-bottom: 16px;
`;

const AboutParagraph = styled.p`
  color: #374151;
  line-height: 1.65;
  margin-bottom: 18px;

  &:last-child {
    margin-bottom: 0;
  }
`;

const toTitleCase = (value: string) =>
  value
    .split(/[\s_-]+/)
    .filter(Boolean)
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1).toLowerCase())
    .join(" ");

const hasText = (value: string | null | undefined) =>
  typeof value === "string" && value.trim().length > 0;

const toThemePath = (value: string) =>
  `/domains/${value
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9\s-]/g, "")
    .replace(/\s+/g, "-")}`;

interface BrandMetric {
  value: number;
  label: string;
}

const toList = (items: string[]) => {
  if (items.length === 0) return "";
  if (items.length === 1) return items[0];
  if (items.length === 2) return `${items[0]} and ${items[1]}`;
  return `${items.slice(0, -1).join(", ")}, and ${items[items.length - 1]}`;
};

// ── Deterministic phrase variation ─────────────────────────────────────────
const domainHash = (s: string): number => {
  let h = 0;
  for (let i = 0; i < s.length; i++) {
    h = (h * 31 + s.charCodeAt(i)) >>> 0;
  }
  return h;
};

const hashPick = <T,>(arr: T[], hash: number, salt: number): T =>
  arr[(hash + salt) % arr.length];

const CATEGORY_CONNECTORS = ["-focused", "-oriented", "-driven", "-related"];

const THEME_CONNECTORS = [
  "with clear associations to",
  "with strong ties to",
  "drawing on themes of",
  "with connections to",
];

const SCORE_OPENERS: Array<(s: number) => string> = [
  (s) => `With a brand score of ${s}/100,`,
  (s) => `Rated ${s}/100 for branding,`,
  (s) => `Scoring ${s}/100 on the brand scale,`,
  (s) => `At a brand score of ${s}/100,`,
];

const STRONG_SCORE_PHRASES = [
  "with strong branding potential",
  "with strong brand appeal",
  "with a high brand score",
  "showing strong brand value",
];

const SOLID_SCORE_PHRASES = [
  "with solid branding potential",
  "with good brand appeal",
  "with a solid brand score",
  "showing good brand value",
];

const USE_CASES: Record<string, string> = {
  "artificial intelligence": "AI tools",
  "machine learning": "ML platforms",
  ai: "AI tools",
  analytics: "analytics tools",
  automation: "automation software",
  blockchain: "blockchain tools",
  cloud: "cloud services",
  communication: "communication tools",
  crypto: "crypto platforms",
  data: "data tools",
  design: "design tools",
  ecommerce: "online stores",
  education: "edtech tools",
  feedback: "feedback platforms",
  finance: "fintech products",
  fitness: "fitness platforms",
  food: "food apps",
  gaming: "gaming platforms",
  health: "health platforms",
  innovation: "experimental products",
  lab: "research tools",
  laboratories: "research platforms",
  marketing: "marketing tools",
  media: "media platforms",
  mobile: "mobile apps",
  productivity: "productivity tools",
  research: "research platforms",
  security: "security software",
  social: "social platforms",
  software: "software products",
  technology: "tech products",
  travel: "travel platforms",
};

const buildDomainSummary = (
  domain: Pick<
    DomainDetails,
    | "domain"
    | "primaryCategory"
    | "structureType"
    | "semanticTokens"
    | "pronounceability"
    | "brandScore"
  >,
  relatedThemes: string[],
): string[] => {
  const h = domainHash(domain.domain);

  const category = hasText(domain.primaryCategory)
    ? toTitleCase(domain.primaryCategory as string).toLowerCase()
    : null;
  const structure = hasText(domain.structureType)
    ? toTitleCase(domain.structureType as string).toLowerCase()
    : null;

  const catConnector = hashPick(CATEGORY_CONNECTORS, h, 0);
  const themeConnector = hashPick(THEME_CONNECTORS, h, 1);

  const intro = (() => {
    const themeText =
      relatedThemes.length > 0
        ? ` ${themeConnector} ${toList(relatedThemes.map((t) => t.toLowerCase()))}`
        : "";

    if (structure && category) {
      const article = /^[aeiou]/i.test(structure) ? "an" : "a";
      return `${domain.domain} is ${article} ${structure}, ${category}${catConnector} domain${themeText}.`;
    }

    if (structure) {
      const article = /^[aeiou]/i.test(structure) ? "an" : "a";
      return `${domain.domain} is ${article} ${structure} domain${themeText}.`;
    }

    if (category) {
      return `${domain.domain} is a ${category}${catConnector} domain${themeText}.`;
    }

    if (relatedThemes.length > 0) {
      return `${domain.domain} has ${themeConnector} ${toList(relatedThemes.map((t) => t.toLowerCase()))}.`;
    }

    return null;
  })();

  const p = domain.pronounceability?.toLowerCase();
  const pronounceability =
    p === "high"
      ? "Its clean, memorable sound makes it easy to say and share — a strong foundation for a recognisable brand."
      : p === "medium"
        ? "Its medium pronounceability gives it a startup-style feel while remaining readable enough for a brand name."
        : p === "low"
          ? "Its abstract style suits projects that value distinctiveness over immediate readability."
          : null;

  const score =
    domain.brandScore !== undefined && domain.brandScore !== null
      ? (() => {
          const s = domain.brandScore;
          const useCases =
            relatedThemes.length > 0
              ? toList(
                  relatedThemes
                    .slice(0, 3)
                    .map(
                      (t) =>
                        USE_CASES[t.toLowerCase()] ??
                        `${t.toLowerCase()} projects`,
                    ),
                )
              : category
                ? `${category} tools and related software`
                : "digital products and software";
          const opener = hashPick(SCORE_OPENERS, h, 2)(s);
          return s >= 75
            ? `${opener} potential use cases include ${useCases}.`
            : s >= 60
              ? `${opener} potential use cases may include ${useCases}.`
              : `${opener} niche use cases may include ${useCases}.`;
        })()
      : null;

  return [intro, pronounceability, score].filter(
    (sentence): sentence is string => Boolean(sentence),
  );
};

const getBrandPotentialLabel = (score: number) => {
  if (score >= 90) return "Excellent brand";
  if (score >= 75) return "Strong brand";
  if (score >= 60) return "Good brand";
  if (score >= 40) return "Moderate brand";
  return "Weak brand";
};

const DomainPage = () => {
  const { domain } = useParams();
  const dispatch = useAppDispatch();

  const [records, setRecords] = useState<string[] | undefined>(undefined);

  useEffect(() => {
    if (domain) dispatch(domainSlice.actions.getDomainDetails(domain));
  }, [dispatch, domain]);

  const gettingDomainDetails = useAppSelector(gettingDomainDetailsSelector);
  const domainDetails = useAppSelector(domainDetailsSelector);
  const getDomainDetailsError = useAppSelector(getDomainDetailsErrorSelector);
  const resellers = useAppSelector(resellersSelector);

  const handleOnRecodsLoaded = useCallback((r: string[]) => setRecords(r), []);

  const titleLoaded = useMemo(
    () =>
      gettingDomainDetails === false && domainDetails?.registered !== undefined,
    [gettingDomainDetails, domainDetails],
  );

  const HeaderActions = useMemo(() => {
    return (
      <Status>
        {gettingDomainDetails === true && (
          <SpinnerWrapper>
            <Spinner size="sm" />
          </SpinnerWrapper>
        )}

        {domain && !gettingDomainDetails && domainDetails?.registered && (
          <WhoisButton
            records={records}
            name={domain}
            onRecordsLoaded={handleOnRecodsLoaded}
          />
        )}

        {domain &&
          !gettingDomainDetails &&
          !domainDetails?.registered &&
          resellers && (
            <PurchaseDropDown resellers={resellers} domain={domain} />
          )}
      </Status>
    );
    // domain, gettingDomainDetails, domainDetails, resellers, records may change frequently
    // but this is a small memo to keep JSX tidy; React will still re-evaluate when deps change.
  }, [
    domain,
    gettingDomainDetails,
    domainDetails,
    resellers,
    records,
    handleOnRecodsLoaded,
  ]);

  const brandScoreMetric = useMemo<BrandMetric | undefined>(() => {
    if (
      !domainDetails ||
      domainDetails.brandScore === undefined ||
      domainDetails.brandScore === null
    ) {
      return undefined;
    }

    return {
      value: domainDetails.brandScore,
      label: getBrandPotentialLabel(domainDetails.brandScore),
    };
  }, [domainDetails]);

  const metaLineParts = useMemo(() => {
    if (!domainDetails) return [];

    const parts: string[] = [];
    if (hasText(domainDetails.primaryCategory)) {
      parts.push(toTitleCase(domainDetails.primaryCategory as string));
    }
    if (hasText(domainDetails.structureType)) {
      parts.push(toTitleCase(domainDetails.structureType as string));
    }
    if (hasText(domainDetails.pronounceability)) {
      parts.push(
        `${toTitleCase(domainDetails.pronounceability as string)} pronounceability`,
      );
    }

    return parts;
  }, [domainDetails]);

  const semanticThemes = useMemo(() => {
    if (!domainDetails?.semanticTokens) return [];

    const uniqueTokens = Array.from(
      new Set(domainDetails.semanticTokens.filter((token) => hasText(token))),
    );

    return uniqueTokens.map((token) => {
      const value = token.trim();
      return {
        label: toTitleCase(value),
        futurePath: toThemePath(value),
      };
    });
  }, [domainDetails]);

  const aboutContent = useMemo(() => {
    if (!domainDetails) return null;

    const lines = buildDomainSummary(
      domainDetails,
      semanticThemes.map((theme) => theme.label),
    );
    if (lines.length === 0) return null;

    return {
      title: `About ${domainDetails.domain || domain || "this domain"}`,
      lines,
    };
  }, [domainDetails, domain, semanticThemes]);

  const showBrandContent = useMemo(
    () =>
      titleLoaded &&
      (brandScoreMetric !== undefined ||
        metaLineParts.length > 0 ||
        semanticThemes.length > 0 ||
        aboutContent !== null),
    [titleLoaded, brandScoreMetric, metaLineParts, semanticThemes, aboutContent],
  );

  const DomainBody = useMemo(() => {
    if (!domain || gettingDomainDetails || !domainDetails) return null;
    return domainDetails?.registered ? (
      <TakenDomainBody domainDetails={domainDetails} />
    ) : (
      <AvailableDomainBody domainDetails={domainDetails} />
    );
  }, [domain, gettingDomainDetails, domainDetails]);

  const isThinPage = useMemo(() => {
    if (!domainDetails) return true;

    // Thin page = domain is available (not registered) and there are no business ideas
    return (
      !!domain &&
      domainDetails.registered === false &&
      (!domainDetails.businessIdeas || domainDetails.businessIdeas.length === 0)
    );
  }, [domain, domainDetails]);

  const detailsLoaded = useMemo(
    () => gettingDomainDetails === false && domainDetails !== undefined,
    [gettingDomainDetails, domainDetails],
  );

  const prerenderedRobots = useMemo(() => {
    if (typeof document === "undefined") return null;
    return document
      .querySelector('meta[name="robots"]')
      ?.getAttribute("content");
  }, []);

  const shouldIndex = useMemo(() => {
    if (!detailsLoaded || !domainDetails) return null;

    if (typeof domainDetails.isIndexable === "boolean") {
      return domainDetails.isIndexable;
    }

    // Backward-compatible fallback while API rolls out isIndexable.
    return !isThinPage;
  }, [detailsLoaded, domainDetails, isThinPage]);

  const robotsContent = useMemo(() => {
    if (shouldIndex === null) {
      return prerenderedRobots ?? "noindex,follow";
    }

    return shouldIndex ? "index,follow" : "noindex,follow";
  }, [shouldIndex, prerenderedRobots]);

  const metaTitle = useMemo(() => {
    if (!domainDetails) return `${domain} - Domain Availability | Domain Findr`;
    return domainDetails.registered
      ? `${domain} - Registered Domain | Domain Findr`
      : `${domain} - Available Domain for Sale | Domain Findr`;
  }, [domain, domainDetails]);

  const metaDescription = useMemo(() => {
    if (!domainDetails)
      return `Check availability and details for ${domain}. Find domain registration info, business ideas, and purchase options.`;

    const h = domainHash(domain ?? "");

    const cat = hasText(domainDetails.primaryCategory)
      ? `${toTitleCase(domainDetails.primaryCategory as string).toLowerCase()}${hashPick(CATEGORY_CONNECTORS, h, 3)}`
      : null;

    const scorePart =
      domainDetails.brandScore != null
        ? domainDetails.brandScore >= 75
          ? hashPick(STRONG_SCORE_PHRASES, h, 4)
          : domainDetails.brandScore >= 60
            ? hashPick(SOLID_SCORE_PHRASES, h, 5)
            : null
        : null;

    const tokens = (domainDetails.semanticTokens ?? [])
      .filter((t) => hasText(t))
      .slice(0, 3)
      .map((t) => t.trim().toLowerCase());
    const semanticPart =
      tokens.length > 0 ? `for ${toList(tokens)} projects` : null;

    const catStr = cat ? ` ${cat}` : "";
    const scoreStr = scorePart ? ` ${scorePart}` : "";
    const semanticStr = semanticPart ? ` ${semanticPart}` : "";

    if (domainDetails.registered) {
      return `${domain} is a registered${catStr} domain${scoreStr}. View WHOIS data, registration details, and ownership information.`;
    }

    const base = `${domain} is an available${catStr} domain${scoreStr}${semanticStr}.`;

    const ideasCount = domainDetails.businessIdeas?.length || 0;
    if (ideasCount > 0) {
      return `${base} Explore ${ideasCount} business idea${
        ideasCount > 1 ? "s" : ""
      } and find the best registrar to buy this domain.`;
    }

    return `${base} Compare prices from top domain registrars and secure this domain today.`;
  }, [domain, domainDetails]);

  return (
    <>
      <Helmet>
        <title>{metaTitle}</title>
        <meta name="description" content={metaDescription} />
        <link rel="canonical" href={`${getSiteUrl()}/domain/${domain}`} />

        {/* Open Graph / Facebook */}
        <meta property="og:type" content="website" />
        <meta property="og:url" content={`${getSiteUrl()}/domain/${domain}`} />
        <meta property="og:title" content={metaTitle} />
        <meta property="og:description" content={metaDescription} />

        {/* Twitter */}
        <meta property="twitter:card" content="summary" />
        <meta
          property="twitter:url"
          content={`${getSiteUrl()}/domain/${domain}`}
        />
        <meta property="twitter:title" content={metaTitle} />
        <meta property="twitter:description" content={metaDescription} />

        {/* Robots is API-driven (with thin-page fallback) and independent of render mode. */}
        {robotsContent && <meta name="robots" content={robotsContent} />}
      </Helmet>
      <Page collapsedView={true} maxWidth={920}>
        <PageWrapper>
          <Header $showBrandContent={showBrandContent}>
            <StyledPageTitle
              $registered={domainDetails?.registered ?? false}
              $loaded={titleLoaded}
              align="left"
            >
              {domain}
            </StyledPageTitle>
            {HeaderActions}
          </Header>

          {brandScoreMetric && (
            <ScoreLine>
              <ScoreValue>{brandScoreMetric.value}/100</ScoreValue>
              <ScoreSeparator aria-hidden="true">·</ScoreSeparator>
              <ScorePotential>{brandScoreMetric.label}</ScorePotential>
            </ScoreLine>
          )}

          {metaLineParts.length > 0 && (
            <MetaLine>{metaLineParts.join(" \u00B7 ")}</MetaLine>
          )}

          {aboutContent && (
            <AboutSection>
              <AboutTitle>{aboutContent.title}</AboutTitle>
              {aboutContent.lines.map((line) => (
                <AboutParagraph key={line}>{line}</AboutParagraph>
              ))}
            </AboutSection>
          )}

          {semanticThemes.length > 0 && (
            <ThemeLine>
              <ThemeLineLabel>Related themes: </ThemeLineLabel>
              {semanticThemes.map((theme) => (
                <ThemeToken
                  key={theme.futurePath}
                  data-future-href={theme.futurePath}
                >
                  {theme.label}
                </ThemeToken>
              ))}
            </ThemeLine>
          )}

          {getDomainDetailsError && (
            <Alert className="mt-3" variant="danger">
              {getDomainDetailsError}
            </Alert>
          )}
          {DomainBody}
        </PageWrapper>
      </Page>
    </>
  );
};

export default memo(DomainPage);
